// The calendar's bars (spec/dashboard.md section 8.5): derived from the time leaves of a condition tree inside a window.
// A TimeRage gives [start, finish) and one bar per repeat cycle; a DayOfTheWeek gives the matching day blocks; a
// TimeOfTheDay gives one stripe per day (wrapping past midnight); an And intersects, an Or unites, a Not inverts inside
// the window; other leaves do not constrain. Instants are server seconds; the day boundaries follow game time (the
// environment offset), like the engine (spec/liveops-engine.md section 5.1).
import type { Json } from '../../api/types';
import type { ContentCatalog } from '../ContentContext';
import { CONDITION_NS, minutesOf } from '../conditions/model';
import { childIds, nodeOf } from '../conditions/ops';

export interface Interval {
  start: number;
  end: number;
  /** a repeat cycle other than the first */
  repeated?: boolean;
}

export interface Window {
  start: number;
  end: number;
}

export interface BarSet {
  intervals: Interval[];
  /** the tree has at least one time leaf */
  hasTime: boolean;
  /** the time leaves can never hold together inside the window */
  never: boolean;
}

const DAY = 86400;
const MAX_DEPTH = 16;
const MAX_CYCLES = 400;

function clip(list: Interval[], w: Window): Interval[] {
  return list.map((i) => ({ ...i, start: Math.max(i.start, w.start), end: Math.min(i.end, w.end) })).filter((i) => i.end > i.start);
}

function normalize(list: Interval[]): Interval[] {
  const sorted = [...list].sort((a, b) => a.start - b.start);
  const out: Interval[] = [];
  for (const i of sorted) {
    const last = out[out.length - 1];
    if (last && i.start <= last.end) {
      last.end = Math.max(last.end, i.end);
      last.repeated = last.repeated && i.repeated;
    } else out.push({ ...i });
  }
  return out;
}

export function intersect(a: Interval[], b: Interval[]): Interval[] {
  const out: Interval[] = [];
  for (const x of a) {
    for (const y of b) {
      const start = Math.max(x.start, y.start);
      const end = Math.min(x.end, y.end);
      if (end > start) out.push({ start, end, repeated: x.repeated || y.repeated });
    }
  }
  return normalize(out);
}

export function union(a: Interval[], b: Interval[]): Interval[] {
  return normalize([...a, ...b]);
}

export function complement(a: Interval[], w: Window): Interval[] {
  const out: Interval[] = [];
  let cursor = w.start;
  for (const i of normalize(clip(a, w))) {
    if (i.start > cursor) out.push({ start: cursor, end: i.start });
    cursor = Math.max(cursor, i.end);
  }
  if (cursor < w.end) out.push({ start: cursor, end: w.end });
  return out;
}

function data(node: { data: unknown } | undefined): Record<string, Json> {
  return (node?.data ?? {}) as Record<string, Json>;
}

/** The game-time day start at or before `t` (server seconds) for the given offset. */
function dayStartOf(t: number, offset: number): number {
  return Math.floor((t + offset) / DAY) * DAY - offset;
}

function timeRange(d: Record<string, Json>, w: Window): Interval[] {
  const start = Number(d.start ?? 0);
  const finish = Number(d.finish ?? 0);
  const repeat = Number(d.repeat ?? 0);
  if (finish <= start) return [];
  if (repeat <= 0) return clip([{ start, end: finish }], w);
  const out: Interval[] = [];
  // the first cycle whose finish is after the window start
  let cycle = Math.max(0, Math.floor((w.start - finish) / repeat) + 1);
  for (let n = 0; n < MAX_CYCLES; n++, cycle++) {
    const cs = start + cycle * repeat;
    if (cs >= w.end) break;
    out.push({ start: cs, end: finish + cycle * repeat, repeated: cycle > 0 });
  }
  return clip(out, w);
}

function dayOfWeek(d: Record<string, Json>, w: Window, offset: number): Interval[] {
  const mask = Number(d.days ?? 0);
  if (mask === 0) return [];
  const out: Interval[] = [];
  for (let day = dayStartOf(w.start, offset); day < w.end; day += DAY) {
    const dow = new Date((day + offset) * 1000).getUTCDay();
    if (mask & (1 << dow)) out.push({ start: day, end: day + DAY });
  }
  return clip(out, w);
}

function timeOfDay(d: Record<string, Json>, w: Window, offset: number): Interval[] {
  const s = minutesOf(d.start) ?? 0;
  const f = minutesOf(d.finish) ?? 0;
  if (s === f) return [];
  const out: Interval[] = [];
  for (let day = dayStartOf(w.start, offset) - DAY; day < w.end; day += DAY) {
    if (s < f) out.push({ start: day + s * 60, end: day + f * 60 });
    else out.push({ start: day + s * 60, end: day + DAY + f * 60 }); // wraps past midnight
  }
  return clip(out, w);
}

/** The bars of a condition tree inside the window. */
export function barsOf(catalog: ContentCatalog, rootId: string | null | undefined, w: Window, offset = 0): BarSet {
  const whole: Interval[] = [{ start: w.start, end: w.end }];
  let hasTime = false;
  const seen = new Set<string>();
  const walk = (id: string | null | undefined, depth: number): Interval[] => {
    if (!id || seen.has(id) || depth > MAX_DEPTH) return whole;
    seen.add(id);
    const node = nodeOf(catalog, id);
    if (!node) return whole;
    const key = node.template.startsWith(CONDITION_NS) ? node.template.slice(CONDITION_NS.length) : node.template;
    const d = data(node);
    switch (key) {
      case 'And': {
        let acc = whole;
        for (const c of childIds(node)) acc = intersect(acc, walk(c, depth + 1));
        return acc;
      }
      case 'Or': {
        const children = childIds(node);
        if (children.length === 0) return whole;
        let acc: Interval[] = [];
        for (const c of children) acc = union(acc, walk(c, depth + 1));
        return acc;
      }
      case 'Not': {
        const child = childIds(node)[0];
        return child ? complement(walk(child, depth + 1), w) : whole;
      }
      case 'TimeRage':
        hasTime = true;
        return timeRange(d, w);
      case 'DayOfTheWeek':
        hasTime = true;
        return dayOfWeek(d, w, offset);
      case 'TimeOfTheDay':
        hasTime = true;
        return timeOfDay(d, w, offset);
      default:
        return whole;
    }
  };
  const intervals = rootId ? walk(rootId, 0) : whole;
  return { intervals, hasTime, never: hasTime && intervals.length === 0 };
}

/**
 * The union of several bar sets (section 8.5: an offer's window is the windows of the events that attach it): the merged,
 * sorted intervals, `hasTime` when any set has a time leaf, `never` when every set is never (an empty list: neither).
 */
export function unionBars(sets: BarSet[]): BarSet {
  let intervals: Interval[] = [];
  for (const s of sets) intervals = union(intervals, s.intervals);
  return { intervals, hasTime: sets.some((s) => s.hasTime), never: sets.length > 0 && sets.every((s) => s.never) };
}

/** The window of `days` whole game-time days starting at the day containing `from`. */
export function windowOf(from: number, days: number, offset = 0): Window {
  const start = dayStartOf(from, offset);
  return { start, end: start + days * DAY };
}

/** The duration tail of an event: each bar extended by `duration`, clipped to the window, minus the bar itself. */
export function tailsOf(bars: Interval[], duration: number, w: Window): Interval[] {
  if (duration <= 0) return [];
  return clip(
    bars.map((b) => ({ start: b.end, end: b.end + duration })),
    w,
  );
}
